import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Slider,
  TextField,
  Button,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowForward, Savings } from '@mui/icons-material';
import { SectionTitle } from '../common';
import { fadeInUp, viewportConfig } from '../../utils/animations';
import { AGENT_COMPARISON, PEGASUS_URLS } from '../../utils/constants';

const SavingsCalculator = () => {
  const [units, setUnits] = useState(AGENT_COMPARISON.defaultUnits);
  const [rent, setRent] = useState(AGENT_COMPARISON.defaultRent);
  const [feePercent, setFeePercent] = useState(AGENT_COMPARISON.agentFeePercent);

  const calculations = useMemo(() => {
    const totalMonthlyRent = units * rent;
    const agentMonthlyCost = totalMonthlyRent * (feePercent / 100);
    const agentAnnualCost = agentMonthlyCost * 12;

    const tier = AGENT_COMPARISON.tiers.find((t) => units <= t.maxUnits);
    const itoramaMonthlyCost = tier ? tier.monthlyCost : 5000;
    const itoramaAnnualCost = itoramaMonthlyCost * 12;

    const annualSavings = agentAnnualCost - itoramaAnnualCost;

    return {
      agentMonthlyCost,
      agentAnnualCost,
      itoramaMonthlyCost,
      itoramaAnnualCost,
      annualSavings: Math.max(0, annualSavings),
    };
  }, [units, rent, feePercent]);

  const formatKES = (amount) => {
    return `KES ${amount.toLocaleString('en-KE')}`;
  };

  const handleExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      id="calculator"
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #1E3A5F 0%, #2E5A8F 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(201, 169, 97, 0.1) 0%, transparent 70%)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <SectionTitle
          overline="Savings Calculator"
          title="How Much Is Your Agent Really Costing You?"
          subtitle="Enter your property details below and see how much you could save by switching to IT-Orama."
          light
        />

        <Box
          component={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={fadeInUp}
        >
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  height: '100%',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                  Your Property Details
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                    Number of Units: {units}
                  </Typography>
                  <Slider
                    value={units}
                    onChange={(_, val) => setUnits(val)}
                    min={1}
                    max={100}
                    step={1}
                    color="secondary"
                    sx={{
                      '& .MuiSlider-thumb': {
                        width: 20,
                        height: 20,
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                    Average Monthly Rent per Unit (KES)
                  </Typography>
                  <TextField
                    type="number"
                    value={rent}
                    onChange={(e) => setRent(Math.max(0, Number(e.target.value)))}
                    fullWidth
                    size="small"
                    inputProps={{ min: 0, step: 1000 }}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                    Agent Fee: {feePercent}% of total rent
                  </Typography>
                  <Slider
                    value={feePercent}
                    onChange={(_, val) => setFeePercent(val)}
                    min={5}
                    max={40}
                    step={1}
                    color="secondary"
                    marks={[
                      { value: 5, label: '5%' },
                      { value: 10, label: '10%' },
                      { value: 20, label: '20%' },
                      { value: 30, label: '30%' },
                      { value: 40, label: '40%' },
                    ]}
                    sx={{
                      '& .MuiSlider-thumb': {
                        width: 20,
                        height: 20,
                      },
                    }}
                  />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Agent Annual Cost
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {formatKES(calculations.agentAnnualCost)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    ({formatKES(calculations.agentMonthlyCost)}/month)
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    IT-Orama Annual Cost
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {formatKES(calculations.itoramaAnnualCost)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    ({formatKES(calculations.itoramaMonthlyCost)}/month)
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(201, 169, 97, 0.1) 0%, rgba(201, 169, 97, 0.05) 100%)',
                    border: '1px solid',
                    borderColor: 'secondary.light',
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Savings sx={{ color: 'secondary.main' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      You Save Per Year
                    </Typography>
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: 'secondary.main',
                      fontSize: { xs: '2rem', md: '2.5rem' },
                    }}
                  >
                    {formatKES(calculations.annualSavings)}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  fullWidth
                  endIcon={<ArrowForward />}
                  onClick={() => handleExternalLink(PEGASUS_URLS.signup)}
                  sx={{ py: 1.5 }}
                >
                  Start Saving Today — Sign Up Free
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default SavingsCalculator;
